import { XMarkIcon } from '@heroicons/react/24/outline'
import { CalendarIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { FC, MouseEvent, useRef, useState } from 'react'

import { DesiredDateType } from '@/api/models/requests/lib/requests.types'
import { cn } from '@/app/utils'
import { useServerTimeStore } from '@/components/entities/common/model/server-time'
import {
  DEFAULT_DATE_ONLY_FORMAT,
  DEFAULT_TIMEZONE
} from '@/components/entities/requests/model/constants'
import { Badge } from '@/components/shared/badge'
import { AppButton } from '@/components/shared/button'
import { TimeSlot, WeekDay } from '@/components/shared/datepicker/model/types'

type Props = {
  timeSlots: TimeSlot[]
  label?: string
  date?: string | Date | null
  timeSlot?: string | null
  currentMonth?: string
  currentWeek?: WeekDay[]
  selectedType?: DesiredDateType | null
  onChange: (value: {
    date: string
    timeSlot?: string | null
    type: DesiredDateType
  }) => void
  isDirty: boolean
}

const DatePickerWeek: FC<Props> = ({
  timeSlots,
  label,
  date = undefined,
  timeSlot = null,
  currentMonth,
  currentWeek = [],
  selectedType = null,
  onChange,
  isDirty
}) => {
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false)
  const [tempSelectedDate, setTempSelectedDate] = useState(date)
  const [tempSelectedTimeSlot, setTempSelectedTimeSlot] = useState<
    string | null
  >(timeSlot)
  const [tempSelectedType, setTempSelectedType] =
    useState<DesiredDateType | null>(selectedType)

  const serverOffsetMs = useServerTimeStore(state => state.serverOffsetMs)

  const buttonRef = useRef<HTMLButtonElement>(null)

  const DesiredDateTypeLabels: Record<DesiredDateType, string> = {
    [DesiredDateType.ANY]: 'Любое время',
    [DesiredDateType.ASAP]: 'Как можно скорее',
    [DesiredDateType.INTERVAL]: '',
    [DesiredDateType.NONE]: ''
  }

  const handleChangeDate = (e: MouseEvent, day: WeekDay) => {
    e.stopPropagation()
    e.preventDefault()

    if (!day.isDisabled) {
      setTempSelectedDate(day.fullDate)
      setTempSelectedTimeSlot(null)
      setTempSelectedType(DesiredDateType.INTERVAL)
    }
  }

  const handleTimeSlotClick = (slot: string) => {
    setTempSelectedTimeSlot(prev => (prev === slot ? null : slot))
  }

  const handleAnytimeClick = () => {
    setTempSelectedType(DesiredDateType.ANY)
    setTempSelectedDate(null)
    setTempSelectedTimeSlot(null)
    setIsDatePickerVisible(false)

    onChange({
      date: '',
      type: DesiredDateType.ANY
    })
  }

  const handleAsapClick = () => {
    setTempSelectedType(DesiredDateType.ASAP)
    setTempSelectedDate(null)
    setTempSelectedTimeSlot(null)
    setIsDatePickerVisible(false)

    onChange({
      date: '',
      type: DesiredDateType.ASAP
    })
  }

  const handleApplyDate = () => {
    if (tempSelectedDate && tempSelectedType === DesiredDateType.INTERVAL) {
      onChange({
        date: tempSelectedDate as string,
        timeSlot: tempSelectedTimeSlot,
        type: DesiredDateType.INTERVAL
      })
    }
    setIsDatePickerVisible(false)
  }

  const handleSelectClick = () => {
    setIsDatePickerVisible(prev => !prev)

    if (tempSelectedType && tempSelectedType !== DesiredDateType.INTERVAL) {
      setTempSelectedDate(null)
      setTempSelectedTimeSlot(null)
    } else if (!tempSelectedDate && date) {
      setTempSelectedDate(date)
      setTempSelectedTimeSlot(timeSlot)
    }
  }

  const handleClose = () => {
    setIsDatePickerVisible(false)

    if (tempSelectedType && tempSelectedType !== DesiredDateType.INTERVAL) {
      setTempSelectedDate(null)
      setTempSelectedTimeSlot(null)
    } else {
      setTempSelectedDate(date)
      setTempSelectedTimeSlot(timeSlot)
    }
  }

  const formatDisplayDate = (dateValue?: string | Date | null) => {
    if (!dateValue) {
      return ''
    }

    let formattedDate = dayjs(dateValue).format('D MMMM')

    if (timeSlot) {
      formattedDate += `, ${timeSlot}`
    }

    return formattedDate
  }

  const isTimeSlotDisabled = (slot: TimeSlot): boolean => {
    if (!tempSelectedDate) {
      return true
    }

    const now = dayjs(Date.now() + serverOffsetMs).tz(DEFAULT_TIMEZONE)
    const selectedDay = dayjs(tempSelectedDate).format(DEFAULT_DATE_ONLY_FORMAT)

    if (selectedDay !== now.format(DEFAULT_DATE_ONLY_FORMAT)) {
      return false
    }

    return now.format('HH:mm') >= slot.startTime
  }

  const getBadgeVariant = (option: DesiredDateType) => {
    return tempSelectedType === option ? 'dark-grey' : 'grey'
  }

  const getDisplayText = () => {
    if (tempSelectedType === DesiredDateType.ANY) {
      return DesiredDateTypeLabels.any
    }
    if (tempSelectedType === DesiredDateType.ASAP) {
      return DesiredDateTypeLabels.asap
    }
    if (tempSelectedType === DesiredDateType.INTERVAL && date) {
      return formatDisplayDate(date)
    }
    return ''
  }

  const isApplyButtonDisabled = () => {
    if (!tempSelectedDate) {
      return true
    }

    return (
      tempSelectedType === DesiredDateType.INTERVAL && !tempSelectedTimeSlot
    )
  }

  const handleClearField = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    setTempSelectedDate(null)
    setTempSelectedTimeSlot(null)
    setTempSelectedType(null)
    setIsDatePickerVisible(false)

    onChange({
      date: '',
      timeSlot: null,
      type: DesiredDateType.NONE
    })
  }
  return (
    <div className="relative h-auto">
      <div className="relative transition-shadow">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleSelectClick}
          className={cn(
            'flex h-[56px] w-full space-x-12 rounded-12 border border-grey-20 bg-white px-16 py-12 text-start',
            (date || tempSelectedType) && 'pb-7'
          )}
        >
          <span className="relative flex h-full w-full flex-col justify-end">
            <span
              className={clsx(
                'absolute left-0 font-pro text-black/50 transition-all',
                isDatePickerVisible || (date && isDirty) || tempSelectedType
                  ? 'top-[-5px] translate-y-0 text-12'
                  : 'top-1/2 -translate-y-1/2 text-16'
              )}
            >
              {label || 'Дата'}
            </span>
            {(date ?? tempSelectedType) && (
              <span className="font-pro text-grey-100 text-16">
                {getDisplayText()}
              </span>
            )}
          </span>

          <span className="pointer-events-none absolute inset-y-0 right-16 flex items-center pr-2">
            {getDisplayText().length === 0 ? (
              <CalendarIcon className="h-16 w-16 text-bronze-40" />
            ) : (
              <button
                type="button"
                data-testid="desired-time-clear-button-id"
                onClick={handleClearField}
                className="pointer-events-auto flex items-center justify-center"
              >
                <XMarkIcon className="relative left-2 h-20 w-20 text-grey-60" />
              </button>
            )}
          </span>
        </button>

        {isDatePickerVisible && (
          <button
            type="button"
            className={cn(
              'absolute z-[51] mt-4 w-[317px] rounded-12 bg-white p-16 text-left shadow-[0px_4px_8px_0px_rgba(0,0,0,0.25)] transition-shadow'
            )}
            onClick={e => e.stopPropagation()}
          >
            {currentMonth && (
              <p className="font-pro font-bold text-grey-100 text-16">
                {currentMonth}
              </p>
            )}

            {currentWeek.length > 0 && (
              <div className="flex justify-center">
                {currentWeek.map(day => {
                  const isSelected =
                    tempSelectedDate &&
                    dayjs(tempSelectedDate).isSame(day.date, 'day')

                  return (
                    <div
                      key={day.fullDate}
                      className="flex flex-col items-center justify-center rounded-8 pt-8 transition-colors"
                    >
                      <div className="flex h-[34px] w-[40px] items-center justify-center capitalize text-grey-70 text-12">
                        {day.dayOfWeek}
                      </div>
                      <button
                        type="button"
                        onClick={e =>
                          !day.isDisabled && handleChangeDate(e, day)
                        }
                        disabled={day.isDisabled}
                        className={clsx(
                          'flex h-[34px] w-[40px] items-center justify-center',
                          day.isDisabled && 'cursor-not-allowed opacity-40',
                          !day.isDisabled && !isSelected && 'hover:bg-grey-10',
                          isSelected && 'rounded-6 bg-bronze-40 text-white',
                          day.isToday && !isSelected && 'text-bronze-40'
                        )}
                      >
                        <span
                          className={clsx(
                            'font-pro text-16',
                            isSelected ? 'text-white' : 'text-grey-100',
                            day.isToday && !isSelected && 'text-bronze-40'
                          )}
                        >
                          {day.displayDate}
                        </span>
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="mt-20 flex flex-wrap gap-8 text-center font-pro text-grey-70 text-12">
              {timeSlots.map(slot => {
                const isDisabled = isTimeSlotDisabled(slot)
                const isSelected = tempSelectedTimeSlot === slot.displayText

                return (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() =>
                      !isDisabled && handleTimeSlotClick(slot.displayText)
                    }
                    disabled={isDisabled}
                    className="disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Badge
                      className="px-16 font-normal"
                      size="normal"
                      variant={isSelected ? 'dark-grey' : 'grey'}
                      disabled={isDisabled}
                    >
                      {slot.displayText}
                    </Badge>
                  </button>
                )
              })}
            </div>
            <AppButton
              variant="secondary"
              size="small"
              className="mt-20 w-full"
              type="button"
              onClick={handleApplyDate}
              disabled={isApplyButtonDisabled()}
            >
              Выбрать
            </AppButton>
          </button>
        )}
      </div>
      <div className="mt-8 flex gap-8">
        <button
          type="button"
          onClick={handleAnytimeClick}
          className="disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Badge
            className="px-16 font-normal"
            size="normal"
            variant={getBadgeVariant(DesiredDateType.ANY)}
          >
            {DesiredDateTypeLabels.any}
          </Badge>
        </button>
        <button
          type="button"
          onClick={handleAsapClick}
          className="disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Badge
            className="px-16 font-normal"
            size="normal"
            variant={getBadgeVariant(DesiredDateType.ASAP)}
          >
            {DesiredDateTypeLabels.asap}
          </Badge>
        </button>
      </div>

      {isDatePickerVisible && (
        <button
          type="button"
          className="fixed inset-0 z-[49]"
          onClick={handleClose}
        />
      )}
    </div>
  )
}

export { DatePickerWeek }
